import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { DemeterDashboardWidgetModel, DemeterUserStoreType, MarketIndicatorSummaryModel } from '../../../../Generated/Raven-Demeter';
import useUserStoreApi from '../../../Apis/Hooks/useUserStoreApiHook';
import useLanguage from '../../../Services/Language/useLanguageHook';
import LinkButton, { LinkButtonType } from '../../Form/Buttons/LinkButton';
import ComponentHeader from '../../Headers/ComponentHeader';
import { ITreeNode } from '../../TreeView/TreeItem';
import TreeView from '../../TreeView/TreeView';
import styles from './CommoditySummaryTableModal.module.scss';

export interface ICommoditySummaryTableModal {
    open: boolean;
    marketIndicatorSummaries: MarketIndicatorSummaryModel[] | null | undefined;
    handleChange: (selected: MarketIndicatorSummaryModel[] | undefined) => void;
    handleClose: () => void;
    dashboardWidget?: DemeterDashboardWidgetModel;
}

const CommoditySummaryTableModal: React.FC<ICommoditySummaryTableModal> = (props: ICommoditySummaryTableModal) => {
    const [translations, translate] = useLanguage();
    const [marketIndicatorSummaries, setMarketIndicatorSummaries] = useState<MarketIndicatorSummaryModel[] | null | undefined>();
    const [selectedMarketIndicatorSummaries, setSelectedMarketIndicatorSummaries] = useState<MarketIndicatorSummaryModel[] | undefined>();

    const [userMarketIndicatorSummariesResponse, setUserMarketIndicatorSummaries] = useUserStoreApi(DemeterUserStoreType.CommoditySummaryTable);

    const equals = (a: any, b: any) => {
        if (a === b) {
            return true;
        }
        if ((a === null || a === undefined) && (b === null || b === undefined)) {
            return true;
        }
        return String(a) === String(b);
    };

    useEffect(() => {
        if (!props.marketIndicatorSummaries || !userMarketIndicatorSummariesResponse || !props.dashboardWidget) {
            return;
        }

        const allSummaries = props.marketIndicatorSummaries ?? [];
        setMarketIndicatorSummaries(allSummaries);

        const selectedSummaryGuids =
            props.dashboardWidget?.demeterDashboardWidgetGuid &&
            userMarketIndicatorSummariesResponse?.userStore?.value?.commoditySummaryTable &&
            props.dashboardWidget.demeterDashboardWidgetGuid in userMarketIndicatorSummariesResponse.userStore.value.commoditySummaryTable
                ? userMarketIndicatorSummariesResponse.userStore.value.commoditySummaryTable[props.dashboardWidget.demeterDashboardWidgetGuid]
                : undefined;

        if (selectedSummaryGuids) {
            const selectedSummaries = allSummaries.filter((marketIndicatorSummary: MarketIndicatorSummaryModel) =>
                selectedSummaryGuids.some((marketIndicatorGuid: string) => equals(marketIndicatorGuid, String(marketIndicatorSummary.marketIndicatorGuid))),
            );
            setSelectedMarketIndicatorSummaries(selectedSummaries);
            props.handleChange(selectedSummaries);
        } else {
            setSelectedMarketIndicatorSummaries(allSummaries);
            props.handleChange(allSummaries);
        }
    }, [props.marketIndicatorSummaries, userMarketIndicatorSummariesResponse, props.dashboardWidget]);

    const marketIndicatorsTreeNodes: ITreeNode[] = useMemo(() => {
        const map = new Map<string, ITreeNode>();

        marketIndicatorSummaries?.forEach((marketIndicator: MarketIndicatorSummaryModel) => {
            const { market, marketIndicatorGuid, displayName } = marketIndicator;

            const marketId = `${market}`;
            const marketName = translations.market[market!] ? translations.market[market!] : market ?? '';
            const marketValue = `${marketId}`;

            const marketIndicatorName = displayName ? translate(displayName) ?? displayName : marketIndicatorGuid ?? '';
            const marketIndicatorId = `${market}|${marketIndicatorGuid}`;
            const marketIndicatorValue = `${marketIndicatorId}`;

            // Market node
            if (marketId && !map.has(marketId)) {
                map.set(marketId, {
                    id: marketId,
                    name: marketName,
                    value: marketValue,
                    parentId: '',
                    parentValue: '',
                    column: 'left',
                    order: 0,
                    displayOrder: 0,
                    children: [],
                    selectedProduct: false,
                    expanded: true,
                });
            }

            const parentNode = marketId ? map.get(marketId)! : null;

            // MarketIndicator node
            if (marketIndicatorId) {
                let marketIndicatorNode = parentNode?.children.find((n) => n.id === marketIndicatorId);
                if (!marketIndicatorNode) {
                    marketIndicatorNode = {
                        id: marketIndicatorId,
                        name: marketIndicatorName,
                        value: marketIndicatorValue,
                        parentId: marketId,
                        parentValue: marketValue,
                        column: 'left',
                        selectedProduct: selectedMarketIndicatorSummaries
                            ? selectedMarketIndicatorSummaries.some((selected: MarketIndicatorSummaryModel) =>
                                  equals(selected.marketIndicatorGuid, marketIndicatorGuid),
                              )
                            : false,
                        expanded: false,
                        order: 0,
                        displayOrder: 0,
                        children: [],
                    };
                    parentNode?.children.push(marketIndicatorNode);
                }
            }
        });

        return Array.from(map.values());
    }, [marketIndicatorSummaries, selectedMarketIndicatorSummaries]);

    const handleSelect = (event: React.MouseEvent<HTMLElement>) => {
        const { id } = event.currentTarget;

        const [, marketIndicatorGuid] = id.split('|');

        if (!marketIndicatorGuid) {
            return;
        }

        setSelectedMarketIndicatorSummaries((prev: MarketIndicatorSummaryModel[] | undefined) => {
            if (!prev) {
                return prev;
            }

            const exists = prev.some((x: MarketIndicatorSummaryModel) => equals(x.marketIndicatorGuid, marketIndicatorGuid));

            if (exists) {
                return prev.filter((x: MarketIndicatorSummaryModel) => !equals(x.marketIndicatorGuid, marketIndicatorGuid));
            }

            const list = marketIndicatorSummaries as MarketIndicatorSummaryModel[];
            const itemToAdd = list.find((x) => equals(x.marketIndicatorGuid, marketIndicatorGuid));

            if (!itemToAdd) {
                return prev;
            }

            return [...prev, itemToAdd];
        });
    };

    const handleChange = () => {
        props.handleChange(selectedMarketIndicatorSummaries);
        const selectedSummaryGuids = selectedMarketIndicatorSummaries?.map((mi) => mi.marketIndicatorGuid ?? '') ?? [];
        const widgetGuid = props.dashboardWidget?.demeterDashboardWidgetGuid;
        if (!widgetGuid) {
            return;
        }
        const currentValue = userMarketIndicatorSummariesResponse?.userStore?.value ?? {};
        const currentCommoditySummaryTable = currentValue.commoditySummaryTable ?? {};
        setUserMarketIndicatorSummaries({
            ...currentValue,
            commoditySummaryTable: {
                ...currentCommoditySummaryTable,
                [String(widgetGuid)]: selectedSummaryGuids,
            },
        });
    };

    return (
        <Dialog open={props.open} onClose={props.handleClose} sx={{ '& .MuiDialog-paper': { maxWidth: '534px' } }}>
            <DialogTitle>
                <div className={styles.market_indicators_modal_header}>
                    <ComponentHeader title="title" />
                    <CloseIcon className={styles.market_indicators_modal_close} onClick={props.handleClose} />
                </div>
            </DialogTitle>
            <DialogContent>
                <div className={styles.market_indicators_modal_content}>
                    <div>description</div>
                    <div className={styles.market_indicators_modal_content_treeview}>
                        <TreeView
                            data={marketIndicatorsTreeNodes}
                            useCheckboxSelection
                            searchTerm=""
                            handleSelect={handleSelect}
                            track={{ eventCategory: 'Dashboard', eventAction: 'ModalCommoditySummaryTable' }}
                        />
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <div className={styles.market_indicators_modal_actions}>
                    <LinkButton
                        title={translations.actions.cancel}
                        type={LinkButtonType.White}
                        onClick={props.handleClose}
                        track={{ eventCategory: 'Dashboard', eventAction: 'ModalCommoditySummaryTableCancelClicked' }}
                    />
                    <LinkButton
                        title={translations.actions.apply}
                        type={LinkButtonType.Blue}
                        onClick={handleChange}
                        track={{ eventCategory: 'Dashboard', eventAction: 'ModalCommoditySummaryTableApplyClicked' }}
                    />
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default CommoditySummaryTableModal;
